import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  BarChart3, TrendingUp, AlertCircle, CheckCircle, DollarSign, Target, Users, Activity, PieChart 
} from 'lucide-react'
import { 
  BarChart, Bar, PieChart as RePieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar 
} from 'recharts'
import { programsService, Program } from '../services/programs'
import { projectsService, Project, ProjectStatus } from '../services/projects'
import { portfoliosService } from '../services/portfolios'
import { usersService, User } from '../services/users'
import { toast } from 'sonner'
import { getErrorMessage, formatCurrency } from '../utils/apiTransformers'
import type { ApiErrorLike } from '../utils/apiError'

export default function ProgramAnalytics() {
  const navigate = useNavigate()
  const [programs, setPrograms] = useState<Program[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [, setPortfolios] = useState<any[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState<'month' | 'quarter' | 'year'>('quarter')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [programsData, projectsData, portfoliosData, usersData] = await Promise.all([
        programsService.list(),
        projectsService.list(),
        portfoliosService.list(),
        usersService.list({ limit: 1000 })
      ])
      setPrograms(programsData)
      setProjects(projectsData)
      setPortfolios(portfoliosData)
      setUsers(usersData)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error loading data:', error)
      toast.error(getErrorMessage(error) || 'Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading program analytics...</p>
        </div>
      </div>
    )
  }

  // Calculate program health data
  const programHealthData = programs.map(program => {
    const programProjects: Project[] = projects.filter(p => p.program_id === program.id)
    const budget = programProjects.reduce((sum, p) => sum + (parseFloat(p.budget || '0')), 0)
    const spent = programProjects.reduce((sum, p) => sum + (parseFloat(p.actual_cost || '0')), 0)
    const completion = programProjects.length > 0 
      ? Math.round(programProjects.reduce((sum, p) => sum + p.progress, 0) / programProjects.length)
      : 0
    
    // Calculate project status counts
    const onTrackProjects = programProjects.filter(p => p.status === 'active' && p.progress >= 50).length
    const atRiskProjects = programProjects.filter(p => p.status === 'active' && p.progress < 50).length
    const delayedProjects = programProjects.filter((p: Project) => {
      if (!p.end_date) return false
      // Only check projects that are not completed or cancelled
      const status: ProjectStatus = p.status
      if (status === 'completed' || status === 'cancelled') return false
      const endDate = new Date(p.end_date)
      // Project is delayed if end date has passed
      return endDate < new Date()
    }).length

    // Calculate health score
    let healthScore = 0
    if (program.status === 'active') {
      const budgetVariance = budget > 0 ? (spent / budget) * 100 : 0
      const scheduleScore = completion
      const projectHealthScore = programProjects.length > 0 
        ? (onTrackProjects / programProjects.length) * 100 
        : 100
      
      healthScore = Math.round((scheduleScore * 0.4) + (projectHealthScore * 0.4) + (Math.max(0, 100 - Math.abs(budgetVariance - 100)) * 0.2))
    } else if (program.status === 'completed') {
      healthScore = 90
    } else if (program.status === 'on_hold') {
      healthScore = 50
    } else {
      healthScore = 30
    }

    const owner = program.owner_id ? users.find(u => u.id === program.owner_id) : null
    const managerName = owner?.full_name || owner?.email || `User ${program.owner_id}` || 'Unassigned'

    // Generate program code (PROG-YYYY-ID)
    const year = new Date(program.created_at).getFullYear()
    const programCode = `PROG-${year}-${String(program.id).padStart(3, '0')}`

    // Map status
    let status = 'Active'
    if (program.status === 'completed') status = 'Active'
    else if (program.status === 'on_hold') status = 'At Risk'
    else if (healthScore < 60) status = 'Critical'
    else if (healthScore < 75) status = 'At Risk'
    else status = 'Active'

    return {
      id: program.id,
      programCode,
      programName: program.name,
      status,
      healthScore,
      projectCount: programProjects.length,
      budget,
      spent,
      completion,
      onTrackProjects,
      atRiskProjects,
      delayedProjects,
      manager: managerName,
      startDate: program.start_date || '',
      endDate: program.end_date || '',
    }
  })

  // Overall portfolio metrics
  const totalPrograms = programHealthData.length
  const activePrograms = programHealthData.filter(p => p.status === 'Active').length
  const atRiskPrograms = programHealthData.filter(p => p.status === 'At Risk').length
  const criticalPrograms = programHealthData.filter(p => p.status === 'Critical').length
  const totalProjects = programHealthData.reduce((sum, p) => sum + p.projectCount, 0)
  const totalBudget = programHealthData.reduce((sum, p) => sum + p.budget, 0)
  const totalSpent = programHealthData.reduce((sum, p) => sum + p.spent, 0)
  const avgHealthScore = totalPrograms > 0 
    ? Math.round(programHealthData.reduce((sum, p) => sum + p.healthScore, 0) / totalPrograms)
    : 0

  // Program status distribution
  const statusDistribution = [
    { name: 'On Track', value: activePrograms, color: '#10B981' },
    { name: 'At Risk', value: atRiskPrograms, color: '#F59E0B' },
    { name: 'Critical', value: criticalPrograms, color: '#EF4444' },
  ]

  // Budget utilization by program
  const budgetData = programHealthData
    .filter(p => p.budget > 0)
    .map(p => ({
      name: p.programCode,
      fullName: p.programName,
      budget: p.budget / 1000000,
      spent: p.spent / 1000000,
      remaining: (p.budget - p.spent) / 1000000,
    }))

  // Program completion trend
  const completionData = programHealthData
    .filter(p => p.projectCount > 0)
    .sort((a, b) => b.completion - a.completion)
    .map(p => ({
      name: p.programCode,
      fullName: p.programName,
      completion: p.completion,
      healthScore: p.healthScore,
    }))

  // Project distribution by program
  const projectDistributionData = programHealthData
    .filter(p => p.projectCount > 0)
    .map(p => ({
      name: p.programCode,
      fullName: p.programName,
      onTrack: p.onTrackProjects,
      atRisk: p.atRiskProjects,
      delayed: p.delayedProjects,
    }))

  // Health score radar chart (top 6 programs)
  const radarData = programHealthData
    .filter(p => p.projectCount > 0)
    .slice(0, 6)
    .map(p => ({
      program: p.programCode,
      healthScore: p.healthScore,
    }))

  const getHealthColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getHealthBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-100'
    if (score >= 60) return 'bg-yellow-100'
    return 'bg-red-100'
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-500'
      case 'At Risk':
        return 'bg-yellow-500'
      case 'Critical':
        return 'bg-red-500'
      default:
        return 'bg-gray-500'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-700 via-slate-600 to-slate-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl mb-3">Program Analytics</h1>
              <p className="text-white/80 text-lg">
                Comprehensive program portfolio health check and performance insights
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedPeriod('month')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedPeriod === 'month'
                    ? 'bg-white text-slate-700'
                    : 'bg-slate-600 text-white hover:bg-slate-500'
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setSelectedPeriod('quarter')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedPeriod === 'quarter'
                    ? 'bg-white text-slate-700'
                    : 'bg-slate-600 text-white hover:bg-slate-500'
                }`}
              >
                Quarter
              </button>
              <button
                onClick={() => setSelectedPeriod('year')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedPeriod === 'year'
                    ? 'bg-white text-slate-700'
                    : 'bg-slate-600 text-white hover:bg-slate-500'
                }`}
              >
                Year
              </button>
            </div>
          </div>

          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
              <div className="flex items-center justify-between mb-2">
                <div className="text-white/70 text-sm">Total Programs</div>
                <Target className="w-5 h-5 text-white/70" />
              </div>
              <div className="text-3xl mb-1">{totalPrograms}</div>
              <div className="text-sm text-white/60">
                {activePrograms} Active • {atRiskPrograms} At Risk • {criticalPrograms} Critical
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
              <div className="flex items-center justify-between mb-2">
                <div className="text-white/70 text-sm">Total Projects</div>
                <Activity className="w-5 h-5 text-white/70" />
              </div>
              <div className="text-3xl mb-1">{totalProjects}</div>
              <div className="text-sm text-white/60">
                Across {totalPrograms} programs
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
              <div className="flex items-center justify-between mb-2">
                <div className="text-white/70 text-sm">Portfolio Budget</div>
                <DollarSign className="w-5 h-5 text-white/70" />
              </div>
              <div className="text-3xl mb-1">{formatCurrency(totalBudget)}</div>
              <div className="text-sm text-white/60">
                {formatCurrency(totalSpent)} spent ({totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0}%)
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
              <div className="flex items-center justify-between mb-2">
                <div className="text-white/70 text-sm">Avg Health Score</div>
                <BarChart3 className="w-5 h-5 text-white/70" />
              </div>
              <div className="text-3xl mb-1">{avgHealthScore}%</div>
              <div className="text-sm text-white/60">
                Portfolio performance index
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Program Status Distribution and Budget Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Program Status Distribution */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl mb-6 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-slate-600" />
              Program Status Distribution
            </h2>
            {statusDistribution.some(s => s.value > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <RePieChart>
                  <Pie
                    data={statusDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RePieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <PieChart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No program status data available</p>
              </div>
            )}
          </div>

          {/* Budget Utilization */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl mb-6 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-slate-600" />
              Budget Utilization by Program
            </h2>
            {budgetData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={budgetData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value: number) => formatCurrency(value * 1000000)}
                    labelFormatter={(label) => {
                      const program = budgetData.find(p => p.name === label)
                      return program?.fullName || label
                    }}
                  />
                  <Legend />
                  <Bar dataKey="spent" fill="#3B82F6" name="Spent" />
                  <Bar dataKey="remaining" fill="#94A3B8" name="Remaining" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No budget data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Program Completion and Health Scores */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Program Completion Progress */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-slate-600" />
              Program Completion Progress
            </h2>
            {completionData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={completionData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis dataKey="name" type="category" />
                  <Tooltip 
                    formatter={(value: number) => `${value}%`}
                    labelFormatter={(label) => {
                      const program = completionData.find(p => p.name === label)
                      return program?.fullName || label
                    }}
                  />
                  <Bar dataKey="completion" fill="#10B981" name="Completion %" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No completion data available</p>
              </div>
            )}
          </div>

          {/* Health Score Radar */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl mb-6 flex items-center gap-2">
              <Activity className="w-5 h-5 text-slate-600" />
              Program Health Score Comparison
            </h2>
            {radarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="program" />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  <Radar name="Health Score" dataKey="healthScore" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No health score data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Project Distribution by Program */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl mb-6 flex items-center gap-2">
            <Target className="w-5 h-5 text-slate-600" />
            Project Status Distribution by Program
          </h2>
          {projectDistributionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={projectDistributionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  labelFormatter={(label) => {
                    const program = projectDistributionData.find(p => p.name === label)
                    return program?.fullName || label
                  }}
                />
                <Legend />
                <Bar dataKey="onTrack" stackId="a" fill="#10B981" name="On Track" />
                <Bar dataKey="atRisk" stackId="a" fill="#F59E0B" name="At Risk" />
                <Bar dataKey="delayed" stackId="a" fill="#EF4444" name="Delayed" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No project distribution data available</p>
            </div>
          )}
        </div>

        {/* Detailed Program Health Table */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl mb-6 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-slate-600" />
            Program Health Check Details
          </h2>
          {programHealthData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 text-sm text-gray-600">Program</th>
                    <th className="text-left py-3 px-4 text-sm text-gray-600">Manager</th>
                    <th className="text-center py-3 px-4 text-sm text-gray-600">Status</th>
                    <th className="text-center py-3 px-4 text-sm text-gray-600">Health Score</th>
                    <th className="text-center py-3 px-4 text-sm text-gray-600">Projects</th>
                    <th className="text-center py-3 px-4 text-sm text-gray-600">Completion</th>
                    <th className="text-right py-3 px-4 text-sm text-gray-600">Budget Spent</th>
                    <th className="text-center py-3 px-4 text-sm text-gray-600">Project Health</th>
                  </tr>
                </thead>
                <tbody>
                  {programHealthData.map((program) => {
                    const budgetPercent = program.budget > 0 ? Math.round((program.spent / program.budget) * 100) : 0
                    
                    return (
                      <tr 
                        key={program.id} 
                        onClick={() => navigate(`/program/${program.id}`)}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="py-4 px-4">
                          <div>
                            <div className="font-medium text-gray-900">{program.programName}</div>
                            <div className="text-sm text-gray-500">{program.programCode}</div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-700">{program.manager}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${getStatusColor(program.status)}`}></div>
                            <span className="text-sm text-gray-700">{program.status}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full ${getHealthBgColor(program.healthScore)}`}>
                            <span className={`font-medium ${getHealthColor(program.healthScore)}`}>
                              {program.healthScore}%
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="text-sm text-gray-900">{program.projectCount}</span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-slate-600 h-2 rounded-full"
                                style={{ width: `${program.completion}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-700">{program.completion}%</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(program.spent)}
                            </div>
                            {program.budget > 0 && (
                              <div className="text-xs text-gray-500">
                                of {formatCurrency(program.budget)} ({budgetPercent}%)
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center gap-2 text-xs">
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              <span className="text-gray-600">{program.onTrackProjects}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                              <span className="text-gray-600">{program.atRiskProjects}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-red-500"></div>
                              <span className="text-gray-600">{program.delayedProjects}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No program data available</p>
            </div>
          )}
        </div>

        {/* Key Insights and Recommendations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Critical Alerts */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              Critical Attention Required
            </h2>
            {programHealthData.filter(p => p.status === 'Critical' || p.healthScore < 60).length > 0 ? (
              <div className="space-y-3">
                {programHealthData
                  .filter(p => p.status === 'Critical' || p.healthScore < 60)
                  .map(program => (
                    <div 
                      key={program.id}
                      onClick={() => navigate(`/program/${program.id}`)}
                      className="border-l-4 border-red-500 bg-red-50 p-4 rounded cursor-pointer hover:bg-red-100 transition-colors"
                    >
                      <div className="font-medium text-gray-900 mb-1">{program.programName}</div>
                      <div className="text-sm text-gray-600 mb-2">
                        Health Score: {program.healthScore}% • {program.delayedProjects} delayed projects
                      </div>
                      <div className="text-xs text-gray-500">
                        Immediate action required for {program.programCode}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No critical programs requiring attention</p>
              </div>
            )}
          </div>

          {/* Top Performers */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Top Performing Programs
            </h2>
            {programHealthData.filter(p => p.healthScore >= 80).length > 0 ? (
              <div className="space-y-3">
                {programHealthData
                  .filter(p => p.healthScore >= 80)
                  .sort((a, b) => b.healthScore - a.healthScore)
                  .map(program => (
                    <div 
                      key={program.id}
                      onClick={() => navigate(`/program/${program.id}`)}
                      className="border-l-4 border-green-500 bg-green-50 p-4 rounded cursor-pointer hover:bg-green-100 transition-colors"
                    >
                      <div className="font-medium text-gray-900 mb-1">{program.programName}</div>
                      <div className="text-sm text-gray-600 mb-2">
                        Health Score: {program.healthScore}% • {program.onTrackProjects}/{program.projectCount} projects on track
                      </div>
                      <div className="text-xs text-gray-500">
                        Excellent performance by {program.manager}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No top performing programs yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

